import { SALT_BYTES, kdfParamsSchema } from './chunk-SA27ENP5.js';
import { base64UrlBytesSchema, base64UrlSchema, uuidSchema, isoDateTimeSchema, key32Schema, notebookModeSchema, docKindSchema } from './chunk-EWGKAU72.js';
import { z } from 'zod';

var API_PREFIX = "/v1";
var ACCESS_TOKEN_TTL_MS = 15 * 60 * 1e3;
var REFRESH_TOKEN_TTL_MS = 30 * 24 * 60 * 60 * 1e3;
var MAX_ATTACHMENT_BYTES = 200 * 1024 * 1024;
var API_ERROR_CODES = [
  "bad_request",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "rate_limited",
  "payload_too_large",
  "internal",
  "device_revoked"
];
var DEVICE_REVOKED_REASONS = [
  /** Disconnected in the device list on another device, or logged out. */
  "user",
  /** The password was changed on another device. */
  "password_change",
  /** Access was restored with the recovery key. */
  "recovery",
  /** A used refresh token was presented again outside the 60 s grace: a sign of theft. */
  "token_reuse",
  /** The account was deleted (a still-valid access token; `/auth/refresh` then answers `unauthorized`). */
  "account_deleted"
];
var deviceRevokedReasonSchema = z.enum(DEVICE_REVOKED_REASONS);
var apiErrorSchema = z.object({
  error: z.object({
    code: z.enum(API_ERROR_CODES),
    message: z.string(),
    /** Present with `device_revoked` (HTTP 401). */
    reason: deviceRevokedReasonSchema.optional(),
    /** For `rate_limited`. */
    retryAfterMs: z.number().int().nonnegative().optional()
  })
});
function isRecord(v) {
  return typeof v === "object" && v !== null;
}
function classifyApiFailure(status, body) {
  if (status === null) return { kind: "network" };
  let json = body;
  if (typeof body === "string") {
    try {
      json = JSON.parse(body);
    } catch {
      json = null;
    }
  }
  if (status === 401 && isRecord(json) && isRecord(json.error)) {
    if (json.error.code === "device_revoked") {
      const reason = deviceRevokedReasonSchema.safeParse(json.error.reason);
      return { kind: "device_revoked", reason: reason.success ? reason.data : "user" };
    }
  }
  const parsed = apiErrorSchema.safeParse(json);
  const error = parsed.success ? parsed.data.error : null;
  if (status === 401) return { kind: "unauthorized" };
  if (status >= 502 && status <= 504 && !error) return { kind: "network" };
  return {
    kind: "error",
    status,
    code: error?.code ?? null,
    message: error?.message ?? `HTTP ${status}`
  };
}
function normalizeEmail(email) {
  return email.trim().toLowerCase();
}
var emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254));
var saltSchema = base64UrlBytesSchema(SALT_BYTES);
var wrappedKeySchema = base64UrlSchema.min(1);
var sessionSchema = z.object({
  userId: uuidSchema,
  /** The session's device (refresh-token family); also the sync `deviceId`. */
  deviceId: z.string().min(1),
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  accessExpiresAt: isoDateTimeSchema,
  wrappedMasterKeyPassword: wrappedKeySchema,
  accountDocId: uuidSchema
});
var preloginRequestSchema = z.object({ email: emailSchema });
var preloginResponseSchema = z.object({ salt: saltSchema, kdf: kdfParamsSchema });
var signupRequestSchema = z.object({
  email: emailSchema,
  salt: saltSchema,
  kdf: kdfParamsSchema,
  loginKey: key32Schema,
  recoveryLoginKey: key32Schema,
  wrappedMasterKeyPassword: wrappedKeySchema,
  wrappedMasterKeyRecovery: wrappedKeySchema,
  userId: uuidSchema,
  accountDocId: uuidSchema,
  deviceName: z.string().min(1).max(200).optional()
});
var loginRequestSchema = z.object({
  email: emailSchema,
  loginKey: key32Schema,
  deviceName: z.string().min(1).max(200)
});
var refreshRequestSchema = z.object({ refreshToken: z.string().min(1) });
var logoutRequestSchema = z.object({ refreshToken: z.string().min(1) });
var recoveryStartRequestSchema = z.object({
  email: emailSchema,
  recoveryLoginKey: key32Schema
});
var recoveryStartResponseSchema = z.object({
  recoveryToken: z.string().min(1),
  wrappedMasterKeyRecovery: wrappedKeySchema,
  userId: uuidSchema
});
var recoveryFinishRequestSchema = z.object({
  recoveryToken: z.string().min(1),
  salt: saltSchema,
  kdf: kdfParamsSchema,
  loginKey: key32Schema,
  wrappedMasterKeyPassword: wrappedKeySchema,
  deviceName: z.string().min(1).max(200).optional()
});
var changePasswordRequestSchema = z.object({
  currentLoginKey: key32Schema,
  salt: saltSchema,
  kdf: kdfParamsSchema,
  loginKey: key32Schema,
  wrappedMasterKeyPassword: wrappedKeySchema
});
var rotateRecoveryKeyRequestSchema = z.object({
  currentLoginKey: key32Schema,
  recoveryLoginKey: key32Schema,
  wrappedMasterKeyRecovery: wrappedKeySchema
});
var SECURITY_EVENT_KINDS = [
  "password_changed",
  "recovery_used",
  "recovery_key_rotated"
];
var securityEventSchema = z.object({
  kind: z.enum(SECURITY_EVENT_KINDS),
  at: isoDateTimeSchema,
  deviceId: z.string().nullable(),
  deviceName: z.string().nullable()
});
var meResponseSchema = z.object({
  userId: uuidSchema,
  email: z.string(),
  accountDocId: uuidSchema,
  createdAt: isoDateTimeSchema
});
var deviceSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  createdAt: isoDateTimeSchema,
  lastSeenAt: isoDateTimeSchema,
  current: z.boolean()
});
var deleteMeRequestSchema = z.object({ loginKey: key32Schema });
var createNotebookRequestSchema = z.object({
  notebookId: uuidSchema,
  mode: notebookModeSchema,
  /** notebookKey wrapped by the masterKey. */
  wrappedKey: wrappedKeySchema,
  /** Raw notebookKey, only for mode = "agent" (crypto.md §8). */
  notebookKey: key32Schema.optional(),
  /**
   * Notebook of a shared space (D-62): `wrappedKey` is then the notebookKey under the
   * spaceKey (crypto.md §11.2).
   */
  spaceId: uuidSchema.optional(),
  /** Version of the spaceKey that wraps `wrappedKey`; not the current one → 409 (D-62). */
  spaceKeyVersion: z.number().int().positive().optional()
}).refine((b) => b.spaceId === void 0 === (b.spaceKeyVersion === void 0), {
  message: "spaceId and spaceKeyVersion go together",
  path: ["spaceKeyVersion"]
}).refine((b) => b.mode === "agent" === (b.notebookKey !== void 0), {
  message: "notebookKey is required for mode=agent and forbidden for mode=private",
  path: ["notebookKey"]
});
var notebookSchema = z.object({
  id: uuidSchema,
  mode: notebookModeSchema,
  createdAt: isoDateTimeSchema,
  wrappedKey: wrappedKeySchema,
  /** The notebook has members (D-54); absent from older servers. */
  shared: z.boolean().optional(),
  /**
   * Shared space of the notebook (D-62): `wrappedKey` is then under the spaceKey, not the
   * masterKey. null or absent = personal.
   */
  spaceId: uuidSchema.nullable().optional()
});
var keyVersionSchema = z.number().int().positive();
var notebookKeyResponseSchema = z.object({
  wrappedKey: wrappedKeySchema,
  /** D-54 key rotation (crypto.md §10.3); absent = version 1, empty chain. */
  version: keyVersionSchema.optional(),
  keyChain: z.array(wrappedKeySchema).optional(),
  keyRotationPending: z.boolean().optional(),
  /** Key version when the rotation became pending; a higher `version` = step 1 done. */
  pendingSinceVersion: keyVersionSchema.nullable().optional()
});
var patchNotebookModeRequestSchema = z.object({ mode: notebookModeSchema, notebookKey: key32Schema.optional() }).refine((b) => b.mode === "agent" === (b.notebookKey !== void 0), {
  message: "notebookKey is required for mode=agent and forbidden for mode=private",
  path: ["notebookKey"]
});
var registerDocRequestSchema = z.object({ docId: uuidSchema, kind: docKindSchema, notebookId: uuidSchema.nullable() }).refine((b) => (b.kind === "account" || b.kind === "space") === (b.notebookId === null), {
  message: "notebookId must be null for account and space documents and set otherwise",
  path: ["notebookId"]
}).refine((b) => b.kind !== "notebook" || b.docId === b.notebookId, {
  message: "a notebook document's docId equals its notebookId",
  path: ["docId"]
});
var moveDocRequestSchema = z.object({ notebookId: uuidSchema });
var authorSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("user"), userId: uuidSchema }),
  z.object({ kind: z.literal("guest"), shareId: uuidSchema }),
  z.object({ kind: z.literal("agent"), tokenId: z.string().min(1) })
]);
var docVersionSchema = z.object({
  seq: z.number().int().nonnegative(),
  createdAt: isoDateTimeSchema,
  deviceId: z.string(),
  /** Who changed the document since the previous version (D-54); absent from older servers. */
  authors: z.array(authorSchema).optional()
});
var docVersionPayloadSchema = z.object({ payload: base64UrlSchema });
var MEMBER_ROLES = ["editor", "viewer"];
var memberRoleSchema = z.enum(MEMBER_ROLES);
var MAX_MEMBERS = 50;
var INVITATION_TTL_MS = 30 * 24 * 60 * 60 * 1e3;
var MAX_INVITATIONS_PER_DAY = 50;
var shareTargetSchema = z.object({
  kind: z.enum(["note", "notebook"]),
  id: uuidSchema
});
var linkTargetSchema = shareTargetSchema;
var accountTargetSchema = z.object({
  kind: z.enum(["note", "notebook", "space"]),
  id: uuidSchema
});
var boxKeySchema = z.object({
  publicKey: key32Schema,
  wrappedSecretKey: wrappedKeySchema
});
var publicKeyResponseSchema = z.object({ userId: uuidSchema, publicKey: key32Schema });
var createInvitationRequestSchema = z.object({
  invitationId: uuidSchema,
  target: shareTargetSchema,
  email: emailSchema,
  role: memberRoleSchema
});
var accountCreateInvitationRequestSchema = createInvitationRequestSchema.extend({
  target: accountTargetSchema
});
var INVITATION_STATUSES = ["pending", "accepted", "declined", "expired"];
var outgoingInvitationSchema = z.object({
  id: uuidSchema,
  email: z.string(),
  role: memberRoleSchema,
  status: z.enum(INVITATION_STATUSES),
  createdAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema
});
var incomingInvitationSchema = z.object({
  id: uuidSchema,
  target: shareTargetSchema,
  from: z.object({ userId: uuidSchema, email: z.string() }),
  role: memberRoleSchema,
  createdAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema
});
var accountIncomingInvitationSchema = incomingInvitationSchema.extend({
  target: accountTargetSchema
});
var memberSchema = z.object({
  userId: uuidSchema,
  email: z.string(),
  role: memberRoleSchema,
  status: z.enum(["active", "awaiting_key"]),
  joinedAt: isoDateTimeSchema,
  accountCreatedAfterInvite: z.boolean()
});
var patchMemberRequestSchema = z.object({ role: memberRoleSchema });
var putMemberKeyRequestSchema = z.object({
  version: keyVersionSchema,
  wrappedKey: wrappedKeySchema
});
var rotateKeyRequestSchema = z.object({
  version: keyVersionSchema.min(2),
  ownerWrappedKey: wrappedKeySchema,
  previousKeyWrap: wrappedKeySchema,
  memberKeys: z.array(z.object({ userId: uuidSchema, wrappedKey: wrappedKeySchema })).max(MAX_MEMBERS),
  notebookKey: key32Schema.optional()
}).refine((b) => new Set(b.memberKeys.map((m) => m.userId)).size === b.memberKeys.length, {
  message: "duplicate userId in memberKeys",
  path: ["memberKeys"]
});
var sharedByMeItemSchema = z.object({
  target: shareTargetSchema,
  keyVersion: keyVersionSchema,
  keyRotationPending: z.boolean(),
  /** See notebookKeyResponseSchema; absent from older servers. */
  pendingSinceVersion: keyVersionSchema.nullable().optional(),
  members: z.number().int().nonnegative(),
  awaitingKey: z.number().int().nonnegative()
});
var accountSharedByMeItemSchema = sharedByMeItemSchema.extend({
  target: accountTargetSchema
});
var sharedNoteKeyResponseSchema = z.object({
  version: keyVersionSchema,
  ownerWrappedKey: wrappedKeySchema,
  keyChain: z.array(wrappedKeySchema)
});
var sharedItemSchema = z.object({
  target: shareTargetSchema,
  owner: z.object({ userId: uuidSchema, email: z.string(), publicKey: key32Schema }),
  role: memberRoleSchema,
  keyVersion: keyVersionSchema,
  /** null while the owner's client has not delivered the key yet. */
  wrappedKey: wrappedKeySchema.nullable(),
  keyChain: z.array(wrappedKeySchema),
  docIds: z.array(uuidSchema),
  addedAt: isoDateTimeSchema,
  /**
   * Agents can read it (✨, display only): the notebook is in agent mode, or
   * the note lies in an owner's notebook in agent mode. Absent from older servers.
   */
  agentsMode: z.boolean().optional()
});
var accountSharedItemSchema = sharedItemSchema.extend({ target: accountTargetSchema });
var sharedSpaceSchema = z.object({
  id: uuidSchema,
  ownerUserId: uuidSchema,
  keyVersion: keyVersionSchema,
  keyRotationPending: z.boolean(),
  pendingSinceVersion: keyVersionSchema.nullable(),
  createdAt: isoDateTimeSchema
});
var createSpaceRequestSchema = z.object({
  spaceId: uuidSchema,
  wrappedKey: wrappedKeySchema
});
var spaceKeyResponseSchema = z.object({
  version: keyVersionSchema,
  wrappedKey: wrappedKeySchema,
  keyChain: z.array(wrappedKeySchema),
  keyRotationPending: z.boolean(),
  pendingSinceVersion: keyVersionSchema.nullable()
});
var spaceNotebookSchema = z.object({
  notebookId: uuidSchema,
  version: keyVersionSchema,
  spaceWrappedKey: wrappedKeySchema,
  keyChain: z.array(wrappedKeySchema),
  mode: notebookModeSchema
});
var putSpaceNotebookRequestSchema = z.object({
  version: keyVersionSchema,
  spaceWrappedKey: wrappedKeySchema
});
var moveOutNotebookRequestSchema = z.object({
  version: keyVersionSchema.min(2),
  ownerWrappedKey: wrappedKeySchema,
  previousKeyWrap: wrappedKeySchema,
  notebookKey: key32Schema.optional()
});
var rotateSpaceRequestSchema = z.object({
  version: keyVersionSchema.min(2),
  ownerWrappedKey: wrappedKeySchema,
  previousKeyWrap: wrappedKeySchema,
  memberKeys: z.array(z.object({ userId: uuidSchema, wrappedKey: wrappedKeySchema })).max(MAX_MEMBERS),
  notebooks: z.array(
    z.object({
      notebookId: uuidSchema,
      version: keyVersionSchema.min(2),
      spaceWrappedKey: wrappedKeySchema,
      previousKeyWrap: wrappedKeySchema,
      notebookKey: key32Schema.optional()
    })
  )
}).refine((b) => new Set(b.memberKeys.map((m) => m.userId)).size === b.memberKeys.length, {
  message: "duplicate userId in memberKeys",
  path: ["memberKeys"]
}).refine((b) => new Set(b.notebooks.map((n) => n.notebookId)).size === b.notebooks.length, {
  message: "duplicate notebookId in notebooks",
  path: ["notebooks"]
});
var createAttachmentRequestSchema = z.object({
  attachmentId: uuidSchema,
  noteId: uuidSchema,
  /** Total ciphertext bytes. */
  size: z.number().int().nonnegative(),
  chunkCount: z.number().int().positive(),
  mime: z.string().min(1).max(255)
});
var attachmentStatusSchema = z.object({
  size: z.number().int().nonnegative(),
  chunkCount: z.number().int().positive(),
  mime: z.string(),
  uploadedChunks: z.array(z.number().int().nonnegative())
});
var SHARE_PERMISSIONS = ["view", "edit"];
var createShareRequestSchema = z.object({
  shareId: uuidSchema,
  target: linkTargetSchema,
  permission: z.enum(SHARE_PERMISSIONS),
  expiresAt: isoDateTimeSchema.nullable().default(null)
});
var shareSchema = z.object({
  id: uuidSchema,
  target: linkTargetSchema,
  permission: z.enum(SHARE_PERMISSIONS),
  expiresAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema
});
var guestSessionResponseSchema = z.object({
  guestToken: z.string().min(1),
  target: linkTargetSchema,
  permission: z.enum(SHARE_PERMISSIONS),
  docIds: z.array(uuidSchema),
  /**
   * The target's key version and older keys, each wrapped by the next newer one (crypto.md §10.3):
   * with the link's key (the current one) they open what was written before a key rotation.
   * Useless without the link's key. Absent from older servers.
   */
  keyVersion: z.number().int().positive().optional(),
  keyChain: z.array(z.string()).optional()
});
var AGENT_PERMISSIONS = ["read", "read_write"];
var createAgentTokenRequestSchema = z.object({
  name: z.string().min(1).max(200),
  notebookIds: z.array(uuidSchema).default([]),
  /** Every agent-mode notebook, including ones switched to that mode later (`notebookIds` is then ignored). */
  allNotebooks: z.boolean().optional(),
  permission: z.enum(AGENT_PERMISSIONS),
  expiresAt: isoDateTimeSchema.nullable().optional()
}).refine((v) => v.allNotebooks || v.notebookIds.length > 0, { path: ["notebookIds"] });
var createAgentTokenResponseSchema = z.object({
  id: uuidSchema,
  /** Shown once. */
  token: z.string().min(1)
});
var agentTokenSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  notebookIds: z.array(uuidSchema),
  allNotebooks: z.boolean().default(false),
  permission: z.enum(AGENT_PERMISSIONS),
  expiresAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
  lastUsedAt: isoDateTimeSchema.nullable()
});
var healthResponseSchema = z.object({ ok: z.literal(true), version: z.string() });
var API_ROUTES = {
  prelogin: { method: "POST", path: "/auth/prelogin" },
  signup: { method: "POST", path: "/auth/signup" },
  login: { method: "POST", path: "/auth/login" },
  refresh: { method: "POST", path: "/auth/refresh" },
  logout: { method: "POST", path: "/auth/logout" },
  recoveryStart: { method: "POST", path: "/auth/recovery/start" },
  recoveryFinish: { method: "POST", path: "/auth/recovery/finish" },
  changePassword: { method: "POST", path: "/auth/password" },
  rotateRecoveryKey: { method: "POST", path: "/auth/recovery-key" },
  me: { method: "GET", path: "/me" },
  securityEvents: { method: "GET", path: "/me/security-events" },
  deleteMe: { method: "DELETE", path: "/me" },
  listDevices: { method: "GET", path: "/devices" },
  deleteDevice: { method: "DELETE", path: "/devices/:id" },
  createNotebook: { method: "POST", path: "/notebooks" },
  listNotebooks: { method: "GET", path: "/notebooks" },
  getNotebookKey: { method: "GET", path: "/notebooks/:id/key" },
  patchNotebookMode: { method: "PATCH", path: "/notebooks/:id/mode" },
  deleteNotebook: { method: "DELETE", path: "/notebooks/:id" },
  registerDoc: { method: "POST", path: "/docs" },
  moveDoc: { method: "PATCH", path: "/docs/:id" },
  deleteDoc: { method: "DELETE", path: "/docs/:id" },
  listDocVersions: { method: "GET", path: "/docs/:id/versions" },
  getDocVersion: { method: "GET", path: "/docs/:id/versions/:seq" },
  createAttachment: { method: "POST", path: "/attachments" },
  putAttachmentChunk: { method: "PUT", path: "/attachments/:id/chunks/:index" },
  getAttachment: { method: "GET", path: "/attachments/:id" },
  getAttachmentChunk: { method: "GET", path: "/attachments/:id/chunks/:index" },
  deleteAttachment: { method: "DELETE", path: "/attachments/:id" },
  createShare: { method: "POST", path: "/shares" },
  listShares: { method: "GET", path: "/shares" },
  deleteShare: { method: "DELETE", path: "/shares/:id" },
  createGuestSession: { method: "POST", path: "/shares/:id/guest" },
  putBoxKey: { method: "PUT", path: "/me/box-key" },
  getBoxKey: { method: "GET", path: "/me/box-key" },
  getPublicKey: { method: "GET", path: "/users/:userId/public-key" },
  createInvitation: { method: "POST", path: "/invitations" },
  listInvitations: { method: "GET", path: "/invitations" },
  deleteInvitation: { method: "DELETE", path: "/invitations/:id" },
  incomingInvitations: { method: "GET", path: "/invitations/incoming" },
  acceptInvitation: { method: "POST", path: "/invitations/:id/accept" },
  declineInvitation: { method: "POST", path: "/invitations/:id/decline" },
  listMembers: { method: "GET", path: "/members/:kind/:id" },
  patchMember: { method: "PATCH", path: "/members/:kind/:id/:userId" },
  deleteMember: { method: "DELETE", path: "/members/:kind/:id/:userId" },
  putMemberKey: { method: "PUT", path: "/members/:kind/:id/:userId/key" },
  rotateKey: { method: "POST", path: "/members/:kind/:id/rotate" },
  getSharedNoteKey: { method: "GET", path: "/members/note/:id/key" },
  rotateKeyDone: { method: "POST", path: "/members/:kind/:id/rotate/done" },
  sharedWithMe: { method: "GET", path: "/shared-with-me" },
  sharedByMe: { method: "GET", path: "/shared-by-me" },
  createSpace: { method: "POST", path: "/spaces" },
  listSpaces: { method: "GET", path: "/spaces" },
  getSpaceKey: { method: "GET", path: "/spaces/:id/key" },
  listSpaceNotebooks: { method: "GET", path: "/spaces/:id/notebooks" },
  putSpaceNotebook: { method: "PUT", path: "/spaces/:id/notebooks/:notebookId" },
  moveOutSpaceNotebook: { method: "POST", path: "/spaces/:id/notebooks/:notebookId/move-out" },
  rotateSpace: { method: "POST", path: "/spaces/:id/rotate" },
  rotateSpaceDone: { method: "POST", path: "/spaces/:id/rotate/done" },
  deleteSpace: { method: "DELETE", path: "/spaces/:id" },
  createAgentToken: { method: "POST", path: "/agent-tokens" },
  listAgentTokens: { method: "GET", path: "/agent-tokens" },
  deleteAgentToken: { method: "DELETE", path: "/agent-tokens/:id" },
  agentTokenAudit: { method: "GET", path: "/agent-tokens/:id/audit" },
  health: { method: "GET", path: "/health" },
  openapi: { method: "GET", path: "/openapi.json" }
};

export { ACCESS_TOKEN_TTL_MS, AGENT_PERMISSIONS, API_ERROR_CODES, API_PREFIX, API_ROUTES, DEVICE_REVOKED_REASONS, INVITATION_STATUSES, INVITATION_TTL_MS, MAX_ATTACHMENT_BYTES, MAX_INVITATIONS_PER_DAY, MAX_MEMBERS, MEMBER_ROLES, REFRESH_TOKEN_TTL_MS, SECURITY_EVENT_KINDS, SHARE_PERMISSIONS, accountCreateInvitationRequestSchema, accountIncomingInvitationSchema, accountSharedByMeItemSchema, accountSharedItemSchema, accountTargetSchema, agentTokenSchema, apiErrorSchema, attachmentStatusSchema, authorSchema, boxKeySchema, changePasswordRequestSchema, classifyApiFailure, createAgentTokenRequestSchema, createAgentTokenResponseSchema, createAttachmentRequestSchema, createInvitationRequestSchema, createNotebookRequestSchema, createShareRequestSchema, createSpaceRequestSchema, deleteMeRequestSchema, deviceRevokedReasonSchema, deviceSchema, docVersionPayloadSchema, docVersionSchema, emailSchema, guestSessionResponseSchema, healthResponseSchema, incomingInvitationSchema, linkTargetSchema, loginRequestSchema, logoutRequestSchema, meResponseSchema, memberRoleSchema, memberSchema, moveDocRequestSchema, moveOutNotebookRequestSchema, normalizeEmail, notebookKeyResponseSchema, notebookSchema, outgoingInvitationSchema, patchMemberRequestSchema, patchNotebookModeRequestSchema, preloginRequestSchema, preloginResponseSchema, publicKeyResponseSchema, putMemberKeyRequestSchema, putSpaceNotebookRequestSchema, recoveryFinishRequestSchema, recoveryStartRequestSchema, recoveryStartResponseSchema, refreshRequestSchema, registerDocRequestSchema, rotateKeyRequestSchema, rotateRecoveryKeyRequestSchema, rotateSpaceRequestSchema, saltSchema, securityEventSchema, sessionSchema, shareSchema, shareTargetSchema, sharedByMeItemSchema, sharedItemSchema, sharedNoteKeyResponseSchema, sharedSpaceSchema, signupRequestSchema, spaceKeyResponseSchema, spaceNotebookSchema, wrappedKeySchema };
//# sourceMappingURL=chunk-PLMCPVKY.js.map
//# sourceMappingURL=chunk-PLMCPVKY.js.map